import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { CalendarService } from 'src/app/services/calendar.service';
import * as CalendarActions from './calendar.actions';
import * as NotificationsActions from '../notifications/notifications.actions';
import { AppState } from '../app.reducer';
import { Store } from '@ngrx/store';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class CalendarEffects {
    getCalendars$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.getCalendars),
        switchMap((action) => this.calendarService.getCalendars(null, action.siteId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.getCalendarsSuccess({ calendars: resp.Data });
                    } else {
                        return CalendarActions.getCalendarsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.getCalendarsFailed({ errorMessage: error.message })))
            )
        )
    ));

    addCalendar$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.addCalendar),
        map(action => action.calendar),
        switchMap((calendar) => this.calendarService.addCalendar(calendar)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.addCalendarSuccess({
                            message: this.translate.instant('CALENDAR_ADD_SUCCESS'),
                            calendar: { ...calendar, Id: resp.Data.Id },
                        });
                    } else {
                        return CalendarActions.addCalendarFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.addCalendarFailed({ errorMessage: error.message })))
            )
        )
    ));

    addCalendarSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.addCalendarSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_exclusion_calendar_completed', params: { page_title: 'Scenarios | Exclusion calendar' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    editCalendar$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.editCalendar),
        map(action => action.calendar),
        switchMap((calendar) => this.calendarService.editCalendar(calendar)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.editCalendarSuccess({
                            message: this.translate.instant('CALENDAR_EDIT_SUCCESS'),
                            calendar,
                        });
                    } else {
                        return CalendarActions.editCalendarFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.editCalendarFailed({ errorMessage: error.message })))
            )
        )
    ));

    editCalendarSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.editCalendarSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'edit_exclusion_calendar_completed', params: { page_title: 'Scenarios | Exclusion calendar' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    deleteCalendar$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.deleteCalendar),
        map(action => action.calendarId),
        switchMap((id) => this.calendarService.deleteCalendar(id)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.deleteCalendarSuccess({
                            message: this.translate.instant('CALENDAR_DELETE_SUCCESS'),
                            id,
                        });
                    } else {
                        return CalendarActions.deleteCalendarFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.deleteCalendarFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteCalendarSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.deleteCalendarSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'delete_exclusion_calendar_completed', params: { page_title: 'Scenarios | Exclusion calendar' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    getExceptions$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.getExceptions),
        switchMap(() => this.calendarService.getExceptions().pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.getExceptionsSuccess({ exceptions: resp.Data });
                    } else {
                        return CalendarActions.getExceptionsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.getExceptionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    addException$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.addException),
        map(action => action.exception),
        switchMap((exception) => this.calendarService.addException(exception)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.addExceptionSuccess({ exception: { ...exception, Id: resp.Data.Id },
                            message: this.translate.instant("DATE_EXCLUSED_ADD_SUCCESS"), });
                    } else {
                        return CalendarActions.addExceptionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.addExceptionFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteException$ = createEffect(() => this.actions$.pipe(
        ofType(CalendarActions.deleteException),
        map(action => action.exceptionId),
        switchMap((id) => this.calendarService.deleteException(id)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return CalendarActions.deleteExceptionSuccess({
                            message: this.translate.instant("DATE_EXCLUSED_REMOVED_SUCCESS"),
                            id: id });
                    } else {
                        return CalendarActions.deleteExceptionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(CalendarActions.deleteExceptionFailed({ errorMessage: error.message })))
            )
        )
    ));

    notifyWarn$ = createEffect(() => this.actions$.pipe(
        ofType(
            CalendarActions.calendarBigWarning
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("CALENDAR", action.message, NotificationLevel.Warning, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            CalendarActions.addCalendarSuccess,
            CalendarActions.editCalendarSuccess,
            CalendarActions.deleteCalendarSuccess,
            CalendarActions.deleteExceptionSuccess,
            CalendarActions.addExceptionSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("CALENDAR", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private calendarService: CalendarService,
        private translate: TranslateService,
        private store: Store<AppState>,
    ) { }
}
